import itiriri from 'itiriri';
import {HSUser, UserDto} from 'server-openapi';

interface UserUtilStores {
  usersStore: ReadonlyMap<string, HSUser>;
}

export class UserUtils {
  private stores: UserUtilStores;
  constructor(apiStores: UserUtilStores) {
    this.stores = apiStores;
  }
  public getUserName = (user?: UserDto) =>
      getUserName(this.stores.usersStore, user);
  public getUserFullNameAndRoleFromUsername = (userLogin?: string | null) =>
    getUserFullNameAndRoleFromUsername(this.stores.usersStore, userLogin);
  public getUserFullNameAndRoleFromSubjectId = (subjectId?: string | null) =>
    getUserFullNameAndRoleFromSubjectId(this.stores.usersStore, subjectId);
  public getUserFullNameAndRoleFromUserOrEmailId = (email?: string | null, hsId?: number | null) =>
    getUserFullNameAndRoleFromUserOrEmailId(this.stores.usersStore, email, hsId);
}

function getUserName(usersStore: ReadonlyMap<string, HSUser>, user?: UserDto) {
  if (!user) {
    return "";
  }

  let result = "";

  if(user.firstName || user.familyName) {
    result = `${user.firstName} ${user.familyName}`;

    if (user.designation) {
      result += ` (${user.designation})`;
    }
    return result;
  }

  // In some cases the ClientUser may not be fully populated (e.g. when needs data from the server to have synced first).
  // If so, and we at least have a SubjectId then fallback to doing a lookup by SubjectId.
  if(user.subjectId) {
    result = getUserFullNameAndRoleFromSubjectId(usersStore, user.subjectId);

     // If didn't find them and got back 'Unknown User' then don't return that to be backwards consistent with how this
     // function used to return nothing.
    if(result === 'Unknown user') {
      return "";
    }
  }

  return result;
}
function getUserFullNameAndRoleFromUsername(usersStore: ReadonlyMap<string, HSUser>, userLogin?: string | null): string {
  const user = itiriri(usersStore.values()).find((user) => user.login === userLogin);
  return user ? `${user.firstName} ${user.familyName}${getDesignationString(user?.designation)}` : userLogin ?? '';
}

function getUserFullNameAndRoleFromSubjectId(usersStore: ReadonlyMap<string, HSUser>, subjectId?: string | null): string {
  const user = itiriri(usersStore.values()).find((user) => user.subjectId === subjectId);
  return user ? `${user.firstName} ${user.familyName}${getDesignationString(user?.designation)}`: 'Unknown user';
}

function getDesignationString(designation: string | null | undefined) {
  if (!designation) {
    return '';
  }

  const designationRole = designation.substring(designation.lastIndexOf(':') + 1);
  switch (designationRole) {
    case 'EnrolledNurse': {
      return ', Enrolled Nurse';
    }
    case 'PersonalCarer': {
      return ', Personal Carer';
    }
    case 'CarerNurse': {
      return ', Carer';
    }
    case 'MedCompCarer': {
      return ', Med Comp Carer';
    }
    case 'MedCompTrained': {
      return ', Med Comp Trained';
    }
    case 'MedAssist': {
      return ', Med Assist';
    }
    case 'Doctor': {
      return ', Doctor';
    }
    case 'RegisteredNurse': {
      return ', Registered Nurse';
    }
    case 'Administrator': {
      return ', Administrator';
    }
    case 'SyncAccount': {
      return ', Sync Account';
    }
    case 'FacilityManager': {
      return ', Facility Manager';
    }
    case 'MpsSupport': {
      return ', MPS Support';
    }
    case 'SystemAdmin': {
      return ', System Admin';
    }
    case 'AgencyNurse': {
      return ', Agency Nurse';
    }
    case 'DisabilitySupportWorker': {
      return ', Disability Support Worker';
    }    
    case 'Receptionist': {
      return ', Reporting';
    }
    case 'ReadOnly': {
      return ', Read Only';
    }
    case 'EndorsedEnrolledNurse': {
      return ', Agency EN';
    }
    case 'FacilityAdmin': {
      return ', Facility Admin';
    }
    case 'SecondChecker': {
      return ', Second Checker';
    }
    case 'BasicUserManagement': {
      return ', Basic User Management';
    }
    case 'AdvancedUserManagement': {
      return ', Advanced User Management';
    }
    default: {
      return '';
    }
  }
}

function getUserFullNameAndRoleFromUserOrEmailId(usersStore: ReadonlyMap<string, HSUser>, email?: string | null, hsId?: number | null) {
  let user: HSUser | undefined;
  if (hsId) {
    user = usersStore.get(hsId.toString());
  }
  if (!user && !!email) {
    user = itiriri(usersStore.values()).find((user) => user.email === email);
  }
  if (!user) {
    return undefined;
  }
  return `${user.firstName} ${user.familyName}${getDesignationString(user.designation)}`;
}
